import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import AppShell from "../components/app/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  UserPlus, Loader2, Link2, Ban, Users, MailCheck, Clock, ShieldCheck,
  MessageSquare, PhoneCall, LayoutGrid, AlertTriangle,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n";

/**
 * RC-WEB-5 GATE 7 — trusted contacts as a real product workflow.
 * The status shown is the EFFECTIVE routing state derived from real backend
 * semantics: an accepted contact with no assigned profile is NOT operational.
 */

const API = "/api";

const STATUS_STYLE = {
  assigned: "border-emerald-300 bg-emerald-50 text-emerald-800",
  accepted: "border-amber-300 bg-amber-50 text-amber-900",
  invited: "border-[#1B84FF]/40 bg-[#1B84FF]/10 text-[#0F6FDB]",
  expired: "border-slate-300 bg-slate-100 text-slate-700",
  declined: "border-slate-300 bg-slate-100 text-slate-700",
  revoked: "border-red-300 bg-red-50 text-red-800",
};

const STATUS_ICON = {
  assigned: ShieldCheck,
  accepted: AlertTriangle,
  invited: Clock,
  expired: Clock,
  declined: Ban,
  revoked: Ban,
};

/** Effective state (never invented: every branch maps to backend data). */
const effectiveStatus = (contact) => {
  if (contact.status === "revoked") return "revoked";
  if (contact.status === "declined") return "declined";
  if (contact.status === "accepted") {
    return (contact.vehicle_ids || []).length > 0 ? "assigned" : "accepted";
  }
  if (contact.status === "invited") {
    const expiry = contact.invite_expires_at ? new Date(contact.invite_expires_at).getTime() : null;
    return expiry && expiry < Date.now() ? "expired" : "invited";
  }
  return contact.status;
};

export default function TrustedContacts() {
  const { token } = useAuth();
  const { t, lang } = useI18n();
  const [contacts, setContacts] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showRevoked, setShowRevoked] = useState(false);
  const [form, setForm] = useState({ invite_email: "", display_name: "", public_label: "" });
  const [lastInvite, setLastInvite] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [contactRes, vehicleRes] = await Promise.all([
        axios.get(`${API}/trusted-contacts`),
        axios.get(`${API}/vehicles`),
      ]);
      setContacts(contactRes.data.contacts || []);
      setProfiles(vehicleRes.data.vehicles || vehicleRes.data || []);
    } catch (_e) {
      toast.error(t("app.trusted.load_error"));
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  useEffect(() => { load(); }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    if (!form.invite_email) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/trusted-contacts`, form);
      setLastInvite(r.data.invite_url || null);
      setForm({ invite_email: "", display_name: "", public_label: "" });
      toast.success(t("app.trusted.invite_created"));
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail === "cannot_invite_yourself"
        ? t("app.trusted.invite_self")
        : t("app.trusted.invite_error"));
    } finally {
      setSaving(false);
    }
  };

  const togglePermission = async (contact, key) => {
    const next = { ...contact.permissions, [key]: !contact.permissions[key] };
    try {
      await axios.put(`${API}/trusted-contacts/${contact.id}/permissions`, { permissions: next });
      setContacts((prev) => prev.map((c) => (c.id === contact.id ? { ...c, permissions: next } : c)));
    } catch (_e) {
      toast.error(t("app.trusted.perm_error"));
    }
  };

  const toggleProfile = async (contact, vehicleId) => {
    const current = contact.vehicle_ids || [];
    const next = current.includes(vehicleId)
      ? current.filter((v) => v !== vehicleId)
      : [...current, vehicleId];
    try {
      await axios.put(`${API}/trusted-contacts/${contact.id}/vehicles`, { vehicle_ids: next });
      setContacts((prev) => prev.map((c) => (c.id === contact.id ? { ...c, vehicle_ids: next } : c)));
      toast.success(t("app.trusted.profiles_saved"));
    } catch (_e) {
      toast.error(t("app.trusted.profiles_error"));
    }
  };

  const saveLabel = async (contact, value) => {
    try {
      const r = await axios.put(`${API}/trusted-contacts/${contact.id}/public-label`, { public_label: value });
      setContacts((prev) => prev.map((c) => (c.id === contact.id
        ? { ...c, public_label: r.data.public_label } : c)));
      toast.success(t("app.trusted.public_label_saved"));
    } catch (_e) {
      toast.error(t("app.trusted.public_label_error"));
    }
  };

  const revoke = async (contact) => {
    if (!window.confirm(t("app.trusted.revoke_confirm"))) return;
    try {
      await axios.delete(`${API}/trusted-contacts/${contact.id}`);
      toast.success(t("app.trusted.revoked_ok"));
      load();
    } catch (_e) {
      toast.error(t("app.trusted.revoke_error"));
    }
  };

  const revokedCount = contacts.filter((c) => c.status === "revoked").length;
  const visibleContacts = showRevoked ? contacts : contacts.filter((c) => c.status !== "revoked");
  const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString(lang) : "—");

  const steps = [
    [UserPlus, t("app.trusted.step_l1"), t("app.trusted.step1")],
    [MailCheck, t("app.trusted.step_l2"), t("app.trusted.step2")],
    [LayoutGrid, t("app.trusted.step_l3"), t("app.trusted.step3")],
    [MessageSquare, t("app.trusted.step_l4"), t("app.trusted.step4")],
    [PhoneCall, t("app.trusted.step_l5"), t("app.trusted.step5")],
  ];

  return (
    <AppShell title={t("app.trusted.title")} subtitle={t("app.trusted.subtitle")} maxWidth="max-w-5xl">
      <div className="min-w-0 space-y-6">
        {/* how it works */}
        <section className="rounded-3xl bg-[#0B2545] p-6 sm:p-7" data-testid="trusted-how-it-works">
          <h2 className="text-lg font-bold text-white">{t("app.trusted.how_title")}</h2>
          <p className="mt-1 max-w-2xl text-sm text-slate-300">{t("app.trusted.intro")}</p>
          <ol className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {steps.map(([Icon, label, text], i) => (
              <li key={i} className="rounded-2xl border border-white/15 bg-white/10 p-3" data-testid={`trusted-step-${i + 1}`}>
                <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-[#1B84FF]/25 text-[#5BC0FF]">
                  <Icon className="h-4 w-4" />
                </span>
                <p className="mt-2 text-[11px] font-bold uppercase tracking-wide text-[#5BC0FF]">{i + 1}. {label}</p>
                <p className="mt-0.5 text-xs font-medium text-slate-100">{text}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* invite */}
        <section aria-labelledby="invite-heading" className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6">
          <h2 id="invite-heading" className="mb-4 flex items-center gap-2 text-base font-semibold text-[#0B2545] md:text-lg">
            <UserPlus className="h-5 w-5 text-[#1B84FF]" aria-hidden="true" /> {t("app.trusted.invite_title")}
          </h2>
          <form onSubmit={invite} className="grid gap-4 md:grid-cols-3" data-testid="trusted-invite-form">
            <div>
              <Label htmlFor="invite_email" className="text-sm text-slate-700">{t("app.trusted.email_label")}</Label>
              <Input id="invite_email" type="email" required value={form.invite_email}
                     onChange={(e) => setForm({ ...form, invite_email: e.target.value })}
                     className="mt-1 border-slate-300 bg-white text-[#0B2545]"
                     placeholder="persona@correo.com" data-testid="trusted-invite-email" />
            </div>
            <div>
              <Label htmlFor="display_name" className="text-sm text-slate-700">{t("app.trusted.name_label")}</Label>
              <Input id="display_name" value={form.display_name}
                     onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                     className="mt-1 border-slate-300 bg-white text-[#0B2545]"
                     data-testid="trusted-invite-name" />
            </div>
            <div>
              <Label htmlFor="public_label" className="text-sm text-slate-700">{t("app.trusted.label_label")}</Label>
              <Input id="public_label" value={form.public_label}
                     onChange={(e) => setForm({ ...form, public_label: e.target.value })}
                     className="mt-1 border-slate-300 bg-white text-[#0B2545]"
                     data-testid="trusted-invite-public-label" />
              <p className="mt-1 text-xs text-slate-600">{t("app.trusted.label_hint")}</p>
            </div>
            <div className="md:col-span-3">
              <Button type="submit" disabled={saving}
                      className="bg-[#1B84FF] font-semibold text-white hover:bg-[#0F6FDB]"
                      data-testid="trusted-invite-submit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : t("app.trusted.submit")}
              </Button>
            </div>
          </form>
          {lastInvite && (
            <div className="mt-4 rounded-xl border border-[#1B84FF]/30 bg-[#1B84FF]/10 p-3" role="status" data-testid="trusted-invite-link">
              <p className="flex items-center gap-2 text-xs text-slate-700">
                <Link2 className="h-4 w-4" aria-hidden="true" /> {t("app.trusted.invite_link")}
              </p>
              <code className="mt-2 block break-all text-xs text-[#0F6FDB]">{lastInvite}</code>
            </div>
          )}
        </section>

        {/* list */}
        <section aria-labelledby="list-heading">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 id="list-heading" className="flex items-center gap-2 text-base font-semibold text-[#0B2545] md:text-lg">
              <Users className="h-5 w-5 text-slate-600" aria-hidden="true" /> {t("app.trusted.list_title")}
            </h2>
            {revokedCount > 0 && (
              <button type="button" onClick={() => setShowRevoked((v) => !v)} aria-pressed={showRevoked}
                      className="text-xs font-semibold text-slate-700 underline hover:text-[#0B2545]"
                      data-testid="trusted-toggle-revoked">
                {showRevoked ? t("app.trusted.hide_revoked") : t("app.trusted.show_revoked", { n: revokedCount })}
              </button>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center py-12" data-testid="trusted-loading">
              <Loader2 className="h-6 w-6 animate-spin text-[#1B84FF]" />
            </div>
          ) : visibleContacts.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="trusted-empty">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
                <Users className="h-8 w-8 text-slate-600" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-[#0B2545]">
                {revokedCount > 0 ? t("app.trusted.empty_none_active") : t("app.trusted.empty_title")}
              </h3>
              <p className="mx-auto max-w-md text-sm text-slate-600">{t("app.trusted.empty_text")}</p>
            </div>
          ) : (
            <ul className="space-y-4" data-testid="trusted-contact-list">
              {visibleContacts.map((contact) => {
                const state = effectiveStatus(contact);
                const StateIcon = STATUS_ICON[state] || Clock;
                const assignedCount = (contact.vehicle_ids || []).length;
                const noPerms = contact.status === "accepted"
                  && !contact.permissions?.receive_messages && !contact.permissions?.receive_calls;
                return (
                  <li key={contact.id} className="rounded-2xl border border-slate-200 bg-white p-5"
                      data-testid={`trusted-contact-${contact.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-[#0B2545]">
                          {contact.display_name || contact.invite_email}
                        </p>
                        <p className="mt-0.5 text-xs text-slate-600">{contact.invite_email}</p>
                        <p className="mt-1 text-xs text-slate-600" data-testid={`trusted-public-label-value-${contact.id}`}>
                          {t("app.trusted.public_label")}: <span className="font-medium text-slate-800">
                            {contact.public_label || contact.effective_public_label || t("app.inbox.trusted_badge")}
                          </span>
                        </p>
                      </div>
                      <span className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${STATUS_STYLE[state] || STATUS_STYLE.declined}`}
                            data-testid={`trusted-status-${contact.id}`}>
                        <StateIcon className="h-3.5 w-3.5" /> {t(`app.trusted.status.${state}`)}
                      </span>
                    </div>

                    <p className="mt-3 rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-700"
                       data-testid={`trusted-status-hint-${contact.id}`}>
                      {t(`app.trusted.status_hint.${state}`)}
                    </p>

                    <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-600">
                      <span>{t("app.trusted.invited_at")}: {fmtDay(contact.created_at)}</span>
                      {contact.accepted_at && <span>{t("app.trusted.accepted_at")}: {fmtDay(contact.accepted_at)}</span>}
                      <span data-testid={`trusted-assigned-count-${contact.id}`}>
                        {assignedCount > 0
                          ? t("app.trusted.assigned_count", { n: assignedCount })
                          : t("app.trusted.assigned_none")}
                      </span>
                    </div>

                    {contact.status === "accepted" && (
                      <>
                        <div className="mt-5 grid gap-4 sm:grid-cols-2">
                          <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-3 py-2.5">
                            <Label htmlFor={`msg-${contact.id}`} className="flex items-center gap-2 text-sm text-slate-700">
                              <MessageSquare className="h-4 w-4 text-[#1B84FF]" /> {t("app.trusted.perm_messages")}
                            </Label>
                            <Switch id={`msg-${contact.id}`}
                                    checked={!!contact.permissions?.receive_messages}
                                    onCheckedChange={() => togglePermission(contact, "receive_messages")}
                                    data-testid={`trusted-toggle-messages-${contact.id}`} />
                          </div>
                          <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-3 py-2.5">
                            <Label htmlFor={`call-${contact.id}`} className="flex items-center gap-2 text-sm text-slate-700">
                              <PhoneCall className="h-4 w-4 text-[#1B84FF]" /> {t("app.trusted.perm_calls")}
                            </Label>
                            <Switch id={`call-${contact.id}`}
                                    checked={!!contact.permissions?.receive_calls}
                                    onCheckedChange={() => togglePermission(contact, "receive_calls")}
                                    data-testid={`trusted-toggle-calls-${contact.id}`} />
                          </div>
                        </div>
                        {noPerms && (
                          <p className="mt-2 text-xs font-medium text-amber-900" data-testid={`trusted-no-perms-${contact.id}`}>
                            {t("app.trusted.perm_none")}
                          </p>
                        )}

                        <div className="mt-5">
                          <Label htmlFor={`label-${contact.id}`} className="text-sm text-slate-700">
                            {t("app.trusted.public_label")}
                          </Label>
                          <Input id={`label-${contact.id}`} defaultValue={contact.public_label || ""}
                                 className="mt-1 border-slate-300 bg-white text-[#0B2545]"
                                 data-testid={`trusted-public-label-${contact.id}`}
                                 onBlur={(e) => {
                                   if (e.target.value !== (contact.public_label || "")) {
                                     saveLabel(contact, e.target.value);
                                   }
                                 }} />
                        </div>

                        <fieldset className="mt-5">
                          <legend className="mb-2 text-sm text-slate-700">{t("app.trusted.assigned_profiles")}</legend>
                          {profiles.length === 0 ? (
                            <p className="text-xs text-slate-600">{t("app.trusted.no_profiles")}</p>
                          ) : (
                            <div className="flex flex-wrap gap-2">
                              {profiles.map((p) => {
                                const active = (contact.vehicle_ids || []).includes(p.id);
                                return (
                                  <button key={p.id} type="button"
                                          onClick={() => toggleProfile(contact, p.id)}
                                          aria-pressed={active}
                                          className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                                            active
                                              ? "border-[#1B84FF] bg-[#1B84FF]/15 text-[#0F6FDB]"
                                              : "border-slate-300 bg-white text-slate-700 hover:border-slate-400"}`}
                                          data-testid={`trusted-profile-${contact.id}-${p.id}`}>
                                    {active ? "✓ " : ""}{p.name || p.public_alias || p.plate_number || p.id.slice(0, 6)}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </fieldset>
                      </>
                    )}

                    {contact.status !== "revoked" && (
                      <Button variant="ghost" onClick={() => revoke(contact)}
                              className="mt-5 text-red-700 hover:bg-red-50 hover:text-red-800"
                              data-testid={`trusted-revoke-${contact.id}`}>
                        <Ban className="mr-2 h-4 w-4" aria-hidden="true" /> {t("app.trusted.revoke")}
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </AppShell>
  );
}
